// BCM-898 · Goods Receipt Dashboard — Resubmit Document dialog, step 2 of 2.
//
// Same shape as Reset Batch's second step (sasl/batchesDetails/
// DocumentResubmissionDialog) — reached after ResubmitReasonDialog (step 1) — the
// batch ribbon (PO Number / Batch Number / CMO / Batch Status), the document
// picker and the comments box, with the title reflecting the reason chosen in
// step 1.
//
// Per the Resubmit Document redesign (design image):
//   • The batch is whatever was selected in step 1 (pre-populated with the launch
//     row, but the user may have switched it), passed in as `selectedBatch`.
//   • Documents are chosen in a Document Type / Action table with an "Add New Row"
//     button, and the first row is PRE-POPULATED with `activeDocType` — the tab
//     open when Resubmit Document was pressed — but stays editable; falls back to
//     the batch's first fetched document when there was no active tab.
// Submitting posts the same A14 updateFGBatchForWorkflowReset payload the
// two-step (ResetBatchDialog -> DocumentResubmissionDialog) flow posts.
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  makeStyles,
  CircularProgress,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AddCircleOutlineIcon from "@material-ui/icons/AddCircleOutline";
import jwtDecode from "jwt-decode";
import { fetchDocuments, resubmitDocuments } from "../../api/goodsReceiptApi";
import { WITH_DIGI } from "./ResubmitReasonDialog";

const useStyles = makeStyles((theme) => ({
  // Dialog renders through a React portal (document.body), outside the
  // dashboard's own DOM subtree — pin the theme's font explicitly rather than
  // leaving it to inheritance, matching the Batch (SASL) dashboard's dialogs.
  
  dialogPaper: { borderRadius: 8, maxWidth: 900, maxHeight: "90vh", overflowX: "hidden", fontFamily: theme.typography.fontFamily },
  dialogTitle: { padding: theme.spacing(2, 3) },
  cancelButton: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    cursor: "pointer",
    color: "#000",
    fontSize: "1rem",
    fontWeight: 500,
    "&:hover": { textDecoration: "underline" },
  },
  titleText: {
    fontWeight: 600,
    fontSize: "1.5rem",
    color: "#000",
    textAlign: "center",
    width: "100%",
  },
 
  dialogContent: { padding: theme.spacing(2, 3), overflowX: "hidden" },
  fieldLabel: {
    fontSize: "0.875rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: "#000",
    display: "block",
  },
  requiredAsterisk: { color: "#FF0000", marginLeft: theme.spacing(0.25) },
  ribbonBox: {
    backgroundColor: "#F5F7FA",
    borderRadius: 4,
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    overflow: "hidden",
  },
  ribbonLabel: { fontWeight: 600, fontSize: "0.8rem", color: "#000" },
  ribbonValue: { fontSize: "0.875rem", color: "#000", marginTop: theme.spacing(0.5) },
  textField: {
    "& .MuiOutlinedInput-root": { height: 40, display: "flex", alignItems: "center" },
    "& .MuiSelect-select": { paddingLeft: 5 },
  },
  tableContainer: { boxShadow: "none", border: "1px solid #E0E0E0", marginTop: theme.spacing(2) },
  tableHeader: { fontWeight: 600, color: "#000", backgroundColor: "#F5F5F5" },
  addRowButton: {
    marginTop: theme.spacing(1.5),
    textTransform: "none",
    color: "#000",
    border: "1px solid #000",
    borderRadius: 20,
    padding: "4px 16px",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
  errorText: { color: "#FF0000", fontSize: "0.75rem", marginTop: theme.spacing(0.5) },
  noteText: { fontSize: "0.75rem", color: "#666", marginTop: theme.spacing(2) },
  sendButton: {
    backgroundColor: "#e36e39",
    color: "#FFFFFF",
    borderRadius: 20,
    padding: "8px 32px",
    textTransform: "none",
    fontWeight: 500,
    "&:hover": { backgroundColor: "#c44d15" },
    "&:disabled": { backgroundColor: "#CCCCCC", color: "#666666" },
  },
  dialogActions: { padding: theme.spacing(2, 3, 4, 3), justifyContent: "center" },
}));

const ResubmitDocumentDialog = ({
  open,
  onClose,
  row,
  detail,
  selectedBatch,
  reasonForReset,
  activeDocType,
  onSuccess,
}) => {
  const classes = useStyles();
  // Prefer the batch selected in step 1; fall back to the launch row/detail.
  const source = { ...(row || {}), ...(detail || {}), ...(selectedBatch || {}) };

  const [availableDocumentTypes, setAvailableDocumentTypes] = useState([]);
  const [fetchingDocuments, setFetchingDocuments] = useState(false);
  // One entry per table row; the first is pre-populated with the current document.
  const [documentRows, setDocumentRows] = useState([{ documentTypeName: "" }]);
  const [documentError, setDocumentError] = useState("");
  const [comments, setComments] = useState("");
  const [commentsError, setCommentsError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [loading, setLoading] = useState(false);
  const cancelRef = useRef(null);
  const submitCancelRef = useRef(null);
  useEffect(() => {
    return () => {
      if (cancelRef.current) cancelRef.current.cancel("leaving resubmit document dialog");
      if (submitCancelRef.current) submitCancelRef.current.cancel("leaving resubmit document dialog");
    };
  }, []);

  const batchInfo = {
    batchNumber: source.vendorBatchNumber || "",
    poNumber: source.scheduleAgreementNumber || "",
    cmo: source.vendorName || source.cmoMember || "",
    batchStatus: source.batchStatusText || source.statusDisplayName || "",
  };

  useEffect(() => {
    if (!open) return;
    setDocumentRows([{ documentTypeName: "" }]);
    setDocumentError("");
    setComments("");
    setCommentsError("");
    setSubmitError("");
  }, [open]);

  // A15 fetchDocuments — the document filter's options + the pre-populated current
  // document (first document received for this batch).
  useEffect(() => {
    if (!open) return;
    const vendorMosymphonyID = source.vendorMosymphonyID;
    const schrockenDeliveryScheduleBatchID = source.schrockenDeliveryScheduleBatchID;
    if (!vendorMosymphonyID || !schrockenDeliveryScheduleBatchID) {
      setAvailableDocumentTypes([]);
      return;
    }
    let cancelled = false;
    if (cancelRef.current) cancelRef.current.cancel("superseded document-list fetch");
    const cancelSource = axios.CancelToken.source();
    cancelRef.current = cancelSource;
    setFetchingDocuments(true);
    fetchDocuments({ vendorMosymphonyID, schrockenDeliveryScheduleBatchID }, cancelSource.token)
      .then((res) => {
        if (cancelled) return;
        const list =
          res.data && res.data.status && Array.isArray(res.data.data) ? res.data.data : [];
        setAvailableDocumentTypes(list);
        // Pre-populate the current document (still editable) once loaded — prefer
        // the tab that was open when Resubmit Document was pressed, falling back
        // to the first fetched document type when that tab's type isn't offered.
        if (list.length) {
          const preferred =
            activeDocType && list.indexOf(activeDocType) !== -1 ? activeDocType : list[0];
          setDocumentRows((prev) =>
            prev.length === 1 && !prev[0].documentTypeName
              ? [{ documentTypeName: preferred }]
              : prev
          );
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        if (!cancelled) setAvailableDocumentTypes([]);
      })
      .finally(() => {
        if (!cancelled) setFetchingDocuments(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, source.vendorMosymphonyID, source.schrockenDeliveryScheduleBatchID]);

  const setRowType = (index, value) => {
    setDocumentRows((prev) => prev.map((r, i) => (i === index ? { documentTypeName: value } : r)));
    setDocumentError("");
  };
  const addRow = () => setDocumentRows((prev) => prev.concat({ documentTypeName: "" }));
  const removeRow = (index) =>
    setDocumentRows((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));

  const handleCommentsChange = (e) => {
    const value = e.target.value;
    if (value.length <= 255) {
      setComments(value);
      setCommentsError("");
    } else {
      setCommentsError("Comments cannot exceed 255 characters");
    }
  };

  const selectedTypes = documentRows.map((r) => r.documentTypeName).filter(Boolean);

  const handleSend = () => {
    setSubmitError("");
    if (selectedTypes.length === 0) {
      setDocumentError("Please select at least one document type");
      return;
    }
    let collaboratorTypeID = 1;
    try {
      const token = localStorage.getItem("authToken");
      if (token) collaboratorTypeID = jwtDecode(token).collaborator_type_id || 1;
    } catch (e) {
      collaboratorTypeID = 1;
    }

    const requestData = [
      {
        schrockenDeliveryScheduleBatchID: source.schrockenDeliveryScheduleBatchID || "",
        poNumber: batchInfo.poNumber,
        batchNumber: batchInfo.batchNumber,
        documentTypesForResubmission: selectedTypes.map((d) => ({
          documentTypeName: d,
          resubmitted: false,
        })),
        comments: comments,
        documentResubmissionRequested: true,
        resubmitDocumentDigitizationRequired: reasonForReset === WITH_DIGI,
        vendorMosymphonyID: source.vendorMosymphonyID || "",
        collaboratorTypeID: collaboratorTypeID,
      },
    ];

    setLoading(true);
    const cancelSource = axios.CancelToken.source();
    submitCancelRef.current = cancelSource;
    resubmitDocuments(requestData, cancelSource.token)
      .then((res) => {
        if (res.data && res.data.status) {
          if (onSuccess) onSuccess();
          if (onClose) onClose();
        } else {
          setSubmitError(
            (res.data && res.data.message) || "Failed to send request. Please try again."
          );
        }
      })
      .catch((err) => {
        if (axios.isCancel(err)) return;
        setSubmitError(
          (err.response && err.response.data && err.response.data.message) ||
            "An error occurred. Please try again."
        );
      })
      .finally(() => setLoading(false));
  };

  const isFormValid = selectedTypes.length > 0;

  // While the POST is in flight the user has no action available on this dialog —
  // not backdrop click, not Escape, not the Cancel link — until it resolves (the
  // dialog's fields are separately disabled below).
  const handleClose = () => {
    if (loading) return;
    if (onClose) onClose();
  };

  // Title mirrors the reason chosen in step 1 (matches DocumentResubmissionDialog).
  const title =
    reasonForReset === WITH_DIGI ? (
      <React.Fragment>
        Document Resubmission with data
        <br />
        extracted from digitalized document
      </React.Fragment>
    ) : (
      reasonForReset || "Resubmit Document"
    );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableBackdropClick={loading}
      disableEscapeKeyDown={loading}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ className: classes.dialogPaper }}
    >
      <DialogTitle disableTypography className={classes.dialogTitle}>
        <Typography
          className={classes.cancelButton}
          onClick={handleClose}
          style={loading ? { opacity: 0.5, cursor: "not-allowed", pointerEvents: "none" } : undefined}
        >
          Cancel <CloseIcon style={{ fontSize: 24, marginLeft: 4, color: "#FF0000" }} />
        </Typography>
        <Typography className={classes.titleText}>{title}</Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        {/* ── Ribbon — the batch selected in step 1 ────────────────────────────── */}
        <div className={classes.ribbonBox}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Typography className={classes.ribbonLabel}>PO Number</Typography>
              <Typography className={classes.ribbonValue}>{batchInfo.poNumber}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography className={classes.ribbonLabel}>Batch Number</Typography>
              <Typography className={classes.ribbonValue}>{batchInfo.batchNumber}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography className={classes.ribbonLabel}>CMO</Typography>
              <Typography className={classes.ribbonValue}>{batchInfo.cmo}</Typography>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Typography className={classes.ribbonLabel}>Batch Status</Typography>
              <Typography className={classes.ribbonValue}>{batchInfo.batchStatus}</Typography>
            </Grid>
          </Grid>
        </div>

        {/* ── Document Type / Action table ─────────────────────────────────────── */}
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableHeader} style={{ width: "70%" }}>
                  Document Type <span className={classes.requiredAsterisk}>*</span>
                </TableCell>
                <TableCell className={classes.tableHeader} align="center" style={{ width: "30%" }}>
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documentRows.map((r, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <FormControl fullWidth variant="outlined">
                      <Select
                        value={r.documentTypeName}
                        displayEmpty
                        className={classes.textField}
                        onChange={(e) => setRowType(index, e.target.value)}
                        disabled={fetchingDocuments || availableDocumentTypes.length === 0 || loading}
                      >
                        <MenuItem value="" disabled>
                          {fetchingDocuments
                            ? "Loading..."
                            : availableDocumentTypes.length === 0
                            ? "No documents to resubmit for this batch"
                            : "Select document type"}
                        </MenuItem>
                        {availableDocumentTypes.map((type, i) => (
                          <MenuItem
                            key={i}
                            value={type}
                            disabled={
                              type !== r.documentTypeName && selectedTypes.indexOf(type) !== -1
                            }
                          >
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => removeRow(index)}
                      disabled={documentRows.length === 1 || loading}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Button
          className={classes.addRowButton}
          startIcon={<AddCircleOutlineIcon />}
          onClick={addRow}
         
          disabled={
            availableDocumentTypes.length === 0 ||
            selectedTypes.length >= availableDocumentTypes.length ||
            loading
          }
        >
          Add New Row
        </Button>

        {documentError && <Typography className={classes.errorText}>{documentError}</Typography>}

        <Typography className={classes.noteText}>
          Note - Re-submission of Post Manufacturing and Pre-Shipment documents is not
          allowed after IBD/GRN is generated.
        </Typography>

        {/* ── Comments ─────────────────────────────────────────────────────────── */}
        <Typography className={classes.fieldLabel} style={{ marginTop: 16 }}>
          Comments
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          variant="outlined"
          placeholder="Enter text here"
          value={comments}
          onChange={handleCommentsChange}
          disabled={loading}
          error={!!commentsError}
          helperText={commentsError || `${comments.length}/255`}
          FormHelperTextProps={{ style: { textAlign: "right", margin: "4px 0 0 0" } }}
        />

        {submitError && <Typography className={classes.errorText}>{submitError}</Typography>}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          onClick={handleSend}
          className={classes.sendButton}
          disabled={!isFormValid || loading || !!commentsError}
        >
          {loading ? (
            <React.Fragment>
              <CircularProgress size={16} style={{ marginRight: 8, color: "#FFFFFF" }} />
              Sending...
            </React.Fragment>
          ) : (
            "Send Request to CMO"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ResubmitDocumentDialog;
